import { Table, TableBody, TableHead, TableRow } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { InboxRounded } from '@mui/icons-material';
import AryNoContentInfoBox from 'framework/base/components/AryNoContentInfoBox';
import AryWrapper from 'framework/base/components/AryWrapper';
import { BodyTableCell, HeadTableCell } from 'framework/desktop/AryTableCell';
import React, { useEffect, useMemo, useState } from 'react';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import useTableSelection from 'framework/base/hooks/useTableSelection';

const Appending = ({ filteredColumns, orderColumns }) =>
  filteredColumns.some((key) => orderColumns[key]?.appending) && (
    <TableRow>
      {filteredColumns.map((key) => {
        const column = orderColumns[key];
        const appendingSx =
          column?.appendingSx && column?.appendingSx(column.key);
        return (
          <BodyTableCell
            key={key}
            sx={{ p: 2, textAlign: 'center', ...appendingSx }}
          >
            {column.appending.renderAppending()}
          </BodyTableCell>
        );
      })}
    </TableRow>
  );

const AryPinnedTable = React.forwardRef(
  (
    {
      data,
      columns,
      pinnedColumnsList,
      noContentBox,
      customNoContent,
      tableSx,
      stickyHeadSx,
      onHover,
      isFlat,
      page,
      pageCount,
      defaultTableSelection,
      handlePopoverOpen,
      ...others
    },
    ref
  ) => {
    const { t } = useFrameworkTranslation();
    const langDirection = t('dir');
    const theme = useTheme();
    const tableSelection = useTableSelection(
      data,
      pinnedColumnsList,
      columns,
      ref
    );

    const [clickedRowIndex, setClickedRowIndex] = useState();
    const [firstSelectedCell, setFirstSelectedCell] = useState(null);

    const handleCellMouseDown = (rowIndex, columnIndex) => {
      handlePopoverOpen();
      tableSelection.setAllCellsBackgroundToTransparent();
      others?.handleResetStatistics();
      setFirstSelectedCell({ row: rowIndex, col: columnIndex });
      tableSelection.cellMouseDown(rowIndex, columnIndex);
    };

    const handleCellMouseEnter = (rowIndex, columnIndex) => {
      tableSelection.cellMouseEnter(rowIndex, columnIndex);
    };

    const handleCellMouseUp = (rowIndex, columnIndex) => {
      tableSelection.cellMouseUp();
      const newSelectedRows = [];
      for (
        let i = Math.min(firstSelectedCell.row, rowIndex);
        i <= Math.max(firstSelectedCell.row, rowIndex);
        i++
      ) {
        newSelectedRows.push(i);
      }

      const newSelectedCols = [];
      for (
        let i = Math.min(firstSelectedCell.col, columnIndex);
        i <= Math.max(firstSelectedCell.col, columnIndex);
        i++
      ) {
        newSelectedCols.push(i);
      }
      const invalidCols = [];
      newSelectedCols.forEach((col) => {
        if (!tableSelection.isColumnValid(newSelectedRows[0], col)) {
          invalidCols.push(col);
        }
      });
      if (newSelectedRows.length > 1) {
        handlePopoverOpen(
          rowIndex,
          columnIndex,
          newSelectedRows,
          newSelectedCols,
          invalidCols
        );
      }
    };

    const handleClickOutsideTable = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        if (
          others?.popOverRef?.current &&
          others?.popOverRef?.current.contains(event.target)
        ) {
          handlePopoverOpen();
        } else {
          tableSelection.setAllCellsBackgroundToTransparent();
        }
      }
    };

    useEffect(() => {
      if (ref) {
        document.addEventListener('click', handleClickOutsideTable);
      }
      return () => {
        document.removeEventListener('click', handleClickOutsideTable);
      };
    }, []);

    const renderBodyTableCell = (row, key, keyIndex, itemIndex) => {
      const column = columns[key];
      const cellSx =
        column?.cellSx &&
        column?.cellSx(
          row,
          column.key,
          itemIndex,
          clickedRowIndex,
          setClickedRowIndex
        );

      return (
        <BodyTableCell
          key={key}
          id={`cell-${itemIndex}-${keyIndex}`}
          dir={langDirection}
          sx={{
            p: 2,
            textAlign: 'center',
            borderRight:
              keyIndex === pinnedColumnsList.length - 1 &&
              langDirection === 'rtl'
                ? '2px solid rgba(224, 224, 224, 1)'
                : '',
            ...cellSx
          }}
          onMouseDown={() => {
            if (others.isTableSelectable) {
              handleCellMouseDown(itemIndex, keyIndex);
            }
          }}
          onMouseEnter={() => {
            if (others.isTableSelectable) {
              handleCellMouseEnter(itemIndex, keyIndex);
            }
          }}
          onMouseUp={() => {
            if (others.isTableSelectable) {
              handleCellMouseUp(itemIndex, keyIndex);
            }
          }}
        >
          {column.renderCell(
            row,
            column.key,
            itemIndex,
            clickedRowIndex,
            setClickedRowIndex,
            page,
            pageCount
          ) !== undefined &&
          column.renderCell(
            row,
            column.key,
            itemIndex,
            clickedRowIndex,
            setClickedRowIndex,
            page,
            pageCount
          ) !== null
            ? column.renderCell(
                row,
                column.key,
                itemIndex,
                clickedRowIndex,
                setClickedRowIndex,
                page,
                pageCount
              )
            : '-'}
        </BodyTableCell>
      );
    };

    const memoizedRows = useMemo(
      () =>
        data?.map((row, itemIndex) => (
          <TableRow
            key={itemIndex}
            sx={{
              borderBottom: itemIndex === data.length - 1 && 'none',
              backgroundColor: isFlat
                ? ''
                : itemIndex % 2 === 1 && theme.table.darkRow.backgroundColor,
              ...row.rowSx
            }}
            onMouseEnter={() => (onHover ? onHover(row) : undefined)}
          >
            {pinnedColumnsList.map((key, keyIndex) =>
              renderBodyTableCell(row, key, keyIndex, itemIndex)
            )}
          </TableRow>
        )),
      [
        data,
        pinnedColumnsList,
        columns,
        langDirection,
        theme,
        isFlat,
        onHover,
        clickedRowIndex,
        page,
        pageCount,
        others
      ]
    );

    return (
      <Table
        ref={ref}
        stickyHeader
        sx={{
          overflowX: 'auto',
          height: !data?.length ? '100%' : '',
          userSelect: !defaultTableSelection && 'none',
          ...tableSx
        }}
      >
        <TableHead sx={stickyHeadSx}>
          <TableRow>
            {pinnedColumnsList.map((key, itemIndex) => {
              const column = columns[key];
              const headSx =
                column?.headSx && column?.headSx(column.key, itemIndex);
              if (column) {
                return (
                  <HeadTableCell
                    key={key}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      backgroundColor: theme.table.header.color,
                      borderRight:
                        langDirection === 'rtl' &&
                        itemIndex === pinnedColumnsList.length - 1 &&
                        pinnedColumnsList.length !== Object.keys(columns).length
                          ? '2px solid rgba(224, 224, 224, 1)'
                          : '',
                      borderLeft:
                        langDirection === 'ltr' &&
                        itemIndex === pinnedColumnsList.length - 1 &&
                        pinnedColumnsList.length !== Object.keys(columns).length
                          ? '2px solid rgba(224, 224, 224, 1)'
                          : '',
                      ...headSx
                    }}
                  >
                    {column.label}
                  </HeadTableCell>
                );
              }
              return null;
            })}
          </TableRow>
        </TableHead>
        {data?.length ||
        pinnedColumnsList.some((key) => columns[key].appending) ? (
          <TableBody>
            {memoizedRows}
            <Appending
              orderColumns={columns}
              filteredColumns={pinnedColumnsList}
            />
          </TableBody>
        ) : (
          <TableBody>
            <TableRow>
              <BodyTableCell
                colSpan={Object.keys(columns).length}
                sx={{ border: 0, height: '100%' }}
              >
                {noContentBox && (
                  <AryWrapper height="100%">
                    {customNoContent ? (
                      <AryNoContentInfoBox
                        text={customNoContent.text}
                        icon={customNoContent.icon}
                        height={customNoContent.height}
                        textVariant={customNoContent.textVariant}
                        buttonProps={customNoContent.buttonProps}
                      />
                    ) : (
                      <AryNoContentInfoBox
                        height="100%"
                        text={t('No Content Message')}
                        icon={InboxRounded}
                        textVariant="h3Medium"
                      />
                    )}
                  </AryWrapper>
                )}
              </BodyTableCell>
            </TableRow>
          </TableBody>
        )}
      </Table>
    );
  }
);

export default AryPinnedTable;
